import {
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    fetchBaseQuery
} from "@reduxjs/toolkit/query";
import helper from "../../constants/helper";
import {forceLogout} from "../modules/user/user.services";


export interface ApiError {
    status: number;
    data: {
        detail?: string;
        message?: string;
        code?: string;
        error_code?: string;
    };
}

export const API_ERRORS = {
    SESSION_NOT_FOUND: "cant find session",
    JWT_EXPIRED: "invalid_token",
    ACCOUNT_SUSPENDED: "account suspended",
    DEVICE_DETECTED: "device_login_detected",
} as const;


let isRefreshing = false;
let refreshPromise: Promise<RefreshTokenResult> | null = null;
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://dev-api.epictv.mx"

interface RefreshTokenResult {
    accessToken?: string;
    refreshToken?: string;
    shouldLogout: boolean;
}

interface RefreshTokenResponse {
    access?: string;
    refresh?: string;
    refresh_token?: string;
}

const createBaseQuery = () =>

fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers) => {
        if (!headers.has("Authorization")) {
            const token = localStorage.getItem(helper.EPIC_JWT);
            if (token) {
                headers.set("Authorization", `Bearer ${token}`);
            }
        }
        return headers;
    }
});

const getRequestUrl = (args: string | FetchArgs): string => (
    typeof args === "string" ? args : args.url
);

const shouldSkipRefresh = (args: string | FetchArgs): boolean => {
    const url = getRequestUrl(args);
    return url === "/token/" || url.includes("/token/refresh/");
};

const shouldLogoutAfterRefreshError = (error?: FetchBaseQueryError): boolean => {
    if (!error) return false;
    if (error.status === 401 || error.status === 403) return true;

    const data = error.data as ApiError["data"] | undefined;
    return (
        data?.error_code === API_ERRORS.JWT_EXPIRED ||
        data?.code === API_ERRORS.JWT_EXPIRED ||
        data?.detail === API_ERRORS.SESSION_NOT_FOUND
    );
};

// Function to refresh tokens
const refreshTokens = async (): Promise<RefreshTokenResult> => {
    const refreshToken = localStorage.getItem(helper.EPIC_REFRESH_TOKEN);

    if (!refreshToken) {
        return {shouldLogout: true};
    }

    try {
        // Create a separate base query for refresh to avoid interceptor loops
        const refreshBaseQuery = fetchBaseQuery({
            baseUrl: API_BASE_URL,
            prepareHeaders: (headers) => {
                headers.set("Content-Type", "application/json");
                return headers;
            }
        });

        const refreshResult = await refreshBaseQuery(
            {
                url: "/token/refresh/",
                method: "POST",
                body: {
                    refresh: refreshToken
                }
            },
            {} as any, // api parameter not used in this context
            {}
        );
        if (refreshResult.error) {
            return {
                shouldLogout: shouldLogoutAfterRefreshError(refreshResult.error as FetchBaseQueryError),
            };
        }

        if (refreshResult.data) {
            const {access, refresh, refresh_token} = refreshResult.data as RefreshTokenResponse;
            const newRefreshToken = refresh_token || refresh;

            if (!access) {
                return {shouldLogout: false};
            }

            // Store the new tokens
            localStorage.setItem(helper.EPIC_JWT, access);
            if (newRefreshToken) {
                localStorage.setItem(helper.EPIC_REFRESH_TOKEN, newRefreshToken);
            }

            return {
                accessToken: access,
                refreshToken: newRefreshToken,
                shouldLogout: false,
            };
        }

        return {shouldLogout: false};
    } catch (error) {
        console.error("Token refresh failed:", error);
        return {shouldLogout: false};
    }
};

export const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
    const baseQuery = createBaseQuery();
    let result = await baseQuery(args, api, extraOptions);

    // Check if the error is due to authentication issues
    if (result.error) {
        const error = result.error as ApiError;

        // Handle account suspension immediately
        if (error.data?.message === API_ERRORS.ACCOUNT_SUSPENDED) {
            handleSessionNotFound();
            return result;
        }
        // Check for JWT expired or 401 unauthorized
        const hasRefreshToken = Boolean(localStorage.getItem(helper.EPIC_REFRESH_TOKEN));
        const isAuthError =
            !shouldSkipRefresh(args) &&
            (
                error.data?.error_code === API_ERRORS.JWT_EXPIRED ||
                error.data?.code === API_ERRORS.JWT_EXPIRED ||
                (error.status === 401 && hasRefreshToken)
            )

        const isDeviceDetectedError = error.data?.error_code === API_ERRORS.DEVICE_DETECTED
        if (isDeviceDetectedError) {
            forceLogout(API_ERRORS.DEVICE_DETECTED);
            return result;
        }
        if (isAuthError) {
            // If already refreshing, wait for the current refresh to complete
            if (isRefreshing && refreshPromise) {
                try {
                    const refreshResult = await refreshPromise;
                    if (!refreshResult.accessToken) {
                        if (refreshResult.shouldLogout) handleAuthError();
                        return result;
                    }
                    // Retry the original request after refresh completes
                    result = await baseQuery(args, api, extraOptions);
                    return result;
                } catch (refreshError) {
                    return result;
                }
            }

            // Start refresh process
            if (!isRefreshing) {
                isRefreshing = true;
                refreshPromise = refreshTokens();

                try {
                    const refreshResult = await refreshPromise;

                    if (refreshResult.accessToken) {
                        // Retry the original request with new token
                        result = await baseQuery(args, api, extraOptions);
                    } else if (refreshResult.shouldLogout) {
                        // Refresh token is invalid or missing, logout user
                        handleAuthError();
                    }
                } catch (refreshError) {
                    void refreshError;
                } finally {
                    isRefreshing = false;
                    refreshPromise = null;
                }
            }
        } else {
            // Handle other API errors
            handleApiError(error);
        }
    }

    return result;
};

// Enhanced error handling logic
const handleApiError = (error: ApiError): void => {
    const {status, data} = error;

    switch (true) {
        case status === 404 && data?.detail === API_ERRORS.SESSION_NOT_FOUND:
            handleSessionNotFound();
            break;
        case status === 401 && data?.code === API_ERRORS.JWT_EXPIRED:
            // This is now handled in baseQueryWithReauth
            break;
        case status >= 400 && status < 500:
            logError(error);
            break;
        case status >= 500:
            logError(error, "Server Error");
            break;
        default:
            logError(error);
    }
};

// Helper functions for specific error cases
const handleSessionNotFound = (): void => {
    localStorage.removeItem(helper.EPIC_JWT);
    localStorage.removeItem(helper.EPIC_REFRESH_TOKEN);
    console.warn("Session not found - tokens cleared");
};

const handleAuthError = (): void => {
    forceLogout?.();
    console.warn("Authentication failed - user logged out");
};

const logError = (error: ApiError, type: any = "API Error"): void => {
    const errorDetails = {
        type,
        status: error.status,
        message: error.data?.message || error.data?.detail || "Unknown error",
        timestamp: new Date().toISOString()
    };

    console.error(errorDetails);

};
